import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIllustrations, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

type AuditLogEmptyStateProps = {
    hasFacets: boolean;
    canWidenWindow: boolean;
    onWidenWindow: () => void;
    onResetFacets: () => void;
};

export const AuditLogEmptyState = (props: AuditLogEmptyStateProps) => {
    const { hasFacets, canWidenWindow, onWidenWindow, onResetFacets } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack alignItems="center" gap={theme.spacing(2)} padding={theme.spacing(4)} flexGrow={1}>
            <FoodsIllustrations illustration="missingData" />
            <Typography variant="h4" textAlign="center">
                {t("settings.audit_log.empty.title", "No result in this window")}
            </Typography>
            <Typography variant="small-regular" color="text.secondary" textAlign="center">
                {t(
                    "settings.audit_log.empty.body",
                    "No entry matches this query. Widen the time window or drop a facet.",
                )}
            </Typography>
            <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap" justifyContent="center">
                {canWidenWindow && (
                    <Button variant="primary" onClick={onWidenWindow}>
                        {t("settings.audit_log.empty.widen", "Widen the window")}
                    </Button>
                )}
                {hasFacets && (
                    <Button variant="secondary" onClick={onResetFacets}>
                        {t("settings.audit_log.empty.reset", "Reset the facets")}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
};

type AuditLogErrorStateProps = {
    onRetry: () => void;
};

export const AuditLogErrorState = (props: AuditLogErrorStateProps) => {
    const { onRetry } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack alignItems="center" gap={theme.spacing(2)} padding={theme.spacing(4)} flexGrow={1}>
            <FoodsIllustrations illustration="informations" />
            <Typography variant="h4" textAlign="center">
                {t("settings.audit_log.error.title", "The audit log could not be loaded")}
            </Typography>
            <Typography variant="small-regular" color="text.secondary" textAlign="center">
                {t("settings.audit_log.error.body", "Something went wrong while fetching the entries. Try again.")}
            </Typography>
            <Button variant="primary" onClick={onRetry}>
                {t("settings.audit_log.error.retry", "Retry")}
            </Button>
        </Stack>
    );
};

const SKELETON_ROWS = 7;
const SKELETON_BARS = [96, 128, 88, 160];

export const AuditLogLoadingState = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack gap={theme.spacing(2)} flexGrow={1}>
            <Typography variant="small-regular" color="text.secondary">
                {t("settings.audit_log.loading", "Loading entries…")}
            </Typography>
            <Stack sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: theme.spacing(1) }}>
                {Array.from({ length: SKELETON_ROWS }, (_unused, rowIndex) => (
                    <Stack
                        key={rowIndex}
                        direction="row"
                        gap={theme.spacing(3)}
                        paddingX={theme.spacing(2)}
                        paddingY={theme.spacing(1)}
                        sx={{
                            borderBottom:
                                rowIndex === SKELETON_ROWS - 1 ? "none" : `1px solid ${theme.palette.grey[200]}`,
                        }}
                    >
                        {SKELETON_BARS.map((width) => (
                            <Box
                                key={width}
                                sx={{
                                    width,
                                    height: theme.spacing(2),
                                    borderRadius: theme.spacing(0.5),
                                    backgroundColor: theme.palette.grey[200],
                                }}
                            />
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};
